import React, { useEffect, useRef } from 'react';

// Replace with your real AdSense publisher ID when available.
const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || '';

/**
 * AdUnit — reusable, journal-styled Google AdSense slot.
 * If no publisher ID is configured, it renders a tasteful "Bookmark" placeholder
 * so the layout is intact and you can drop the ID in later.
 *
 * variant: 'banner' (footer) | 'bookmark' (edge of spread)
 */
export default function AdUnit({ slot = '', variant = 'banner', className = '' }) {
    const ref = useRef(null);
    const configured = Boolean(ADSENSE_CLIENT && slot);

    useEffect(() => {
        if (!configured) return;
        // Load adsbygoogle.js once
        if (!document.querySelector('script[data-adsbygoogle]')) {
            const s = document.createElement('script');
            s.async = true;
            s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
            s.crossOrigin = 'anonymous';
            s.setAttribute('data-adsbygoogle', 'true');
            document.head.appendChild(s);
        }
        try {
            // eslint-disable-next-line no-unused-expressions
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {}
    }, [configured]);

    if (configured) {
        return (
            <ins
                className={`adsbygoogle block ${className}`}
                data-testid="ad-unit"
                style={{ display: 'block' }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={slot}
                data-ad-format="auto"
                data-full-width-responsive="true"
                // contextual targeting: Education / College Prep
                data-ad-channel="education,college-prep,entrance-exams"
                ref={ref}
            />
        );
    }

    // ---- Placeholder (no publisher ID yet) ----
    if (variant === 'bookmark') {
        return (
            <div
                data-testid="ad-unit"
                className={`relative select-none ${className}`}
                title="Sponsored bookmark"
            >
                <div
                    className="rounded-b-sm px-2 py-3 text-center rotate-0"
                    style={{
                        background: 'linear-gradient(180deg, var(--lavender) 0%, var(--lavender-soft) 100%)',
                        boxShadow: '2px 2px 6px rgba(59,68,30,0.3)',
                        writingMode: 'vertical-rl',
                    }}
                >
                    <span className="mono text-[9px] text-[color:var(--moss-dark)] tracking-widest">
                        SPONSORED · EdPrep
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="ad-unit"
            className={`relative border border-dashed border-[color:var(--parchment)]/30 rounded-sm p-4 text-center ${className}`}
        >
            <div className="mono text-[9px] text-[color:var(--parchment)]/50 tracking-widest">
                ADVERTISEMENT · EDUCATION
            </div>
            <div className="mono text-[8px] text-[color:var(--parchment)]/30 mt-1">
                AdSense slot — add REACT_APP_ADSENSE_CLIENT to activate
            </div>
        </div>
    );
}
